import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { SoundId, SoundPlayer, SoundResolver } from './types';

// ── Real player ───────────────────────────────────────────────────────────────

class WebSoundPlayer implements SoundPlayer {
  private _oggSupported: boolean | null = null;

  constructor(
    private readonly resolver: SoundResolver,
    private readonly volume: number
  ) {}

  private get ext(): 'ogg' | 'mp3' {
    if (this._oggSupported === null) {
      const a = document.createElement('audio');
      this._oggSupported = a.canPlayType('audio/ogg; codecs="opus"') !== '';
    }
    return this._oggSupported ? 'ogg' : 'mp3';
  }

  play(soundId: SoundId): void {
    const path = this.resolver(soundId);
    if (!path) return;
    const audio = new Audio(`${path}.${this.ext}`);
    audio.volume = this.volume;
    // Browsers block autoplay until first user gesture — silently ignore.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    audio.play().catch(() => {});
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP_PLAYER: SoundPlayer = { play: () => {} };
export const SoundContext = createContext<SoundPlayer>(NOOP_PLAYER);

// ── Provider ──────────────────────────────────────────────────────────────────

export interface SoundProviderProps {
  /**
   * Maps a semantic SoundId to an asset path WITHOUT extension.
   * Return null to silence that ID.
   */
  resolver: SoundResolver;
  /** Master volume [0–1]. Default 0.5. */
  volume?: number;
  children: ReactNode;
}

export function SoundProvider({ resolver, volume = 0.5, children }: SoundProviderProps) {
  const player = useMemo(() => new WebSoundPlayer(resolver, volume), [resolver, volume]);

  return <SoundContext.Provider value={player}>{children}</SoundContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the active SoundPlayer.
 * Falls back to a no-op player when rendered outside a SoundProvider
 * (e.g. in tests or Storybook stories that don't mount the provider).
 */
export function useSound(): SoundPlayer {
  return useContext(SoundContext);
}
